import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Input, Select, Button } from "antd";
import { PlusOutlined, DeleteOutlined, UnorderedListOutlined, CloseOutlined, CopyOutlined } from "@ant-design/icons";
import NodeNameInput from "./NodeNameInput";

const { Option } = Select;

const secUid = () => `sec_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
const rowUid = () => `row_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const InteractiveListNode = ({ data, selected, id }) => {
  const { deleteElements } = useReactFlow();
  const emit = (patch) => data.onChange?.({ ...data, ...patch });

  const sections = data.sections || [];

  const addSection = () => {
    emit({
      sections: [
        ...sections,
        { id: secUid(), title: "", rows: [{ id: rowUid(), title: "", description: "" }] },
      ],
    });
  };

  const removeSection = (si) => {
    emit({ sections: sections.filter((_, i) => i !== si) });
  };

  const updateSection = (si, field, value) => {
    emit({
      sections: sections.map((s, i) => i === si ? { ...s, [field]: value } : s),
    });
  };

  const addRow = (si) => {
    emit({
      sections: sections.map((s, i) =>
        i !== si ? s : { ...s, rows: [...(s.rows || []), { id: rowUid(), title: "", description: "" }] }
      ),
    });
  };

  const removeRow = (si, ri) => {
    emit({
      sections: sections.map((s, i) =>
        i !== si ? s : { ...s, rows: (s.rows || []).filter((_, j) => j !== ri) }
      ),
    });
  };

  const updateRow = (si, ri, field, value) => {
    emit({
      sections: sections.map((s, i) =>
        i !== si
          ? s
          : { ...s, rows: (s.rows || []).map((r, j) => j !== ri ? r : { ...r, [field]: value }) }
      ),
    });
  };

  return (
    <div
      style={{
        background: "#fff",
        border: `2px solid ${selected ? "#06b6d4" : "#e5e7eb"}`,
        borderRadius: 10,
        padding: 14,
        minWidth: 320,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <NodeResizer minWidth={240} minHeight={80} isVisible={selected} color="#06b6d4" />
      <Handle type="target" position={Position.Left} style={{ background: "#06b6d4" }} />

      {/* Title */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <UnorderedListOutlined style={{ color: "#06b6d4", fontSize: 16 }} />
        <strong style={{ fontSize: 13 }}>Interactive List</strong>
        <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => data.onDuplicate?.()} style={{ marginLeft: "auto", color: "#9ca3af" }} />
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => deleteElements({ nodes: [{ id }] })} style={{ color: "#9ca3af" }} />
      </div>

      <div
        className="nodrag"
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
      <NodeNameInput data={data} />

      {/* Header */}
      <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
        Header (Optional)
      </label>
      <div>
        <Select
          value={data.headerType || "none"}
          onChange={(v) => emit({ headerType: v })}
          size="small"
          style={{ width: "100%", marginBottom: data.headerType === "text" ? 4 : 8 }}
        >
          <Option value="none">None</Option>
          <Option value="text">Text</Option>
        </Select>
      </div>
      {data.headerType === "text" && (
        <Input
          value={data.headerText || ""}
          onChange={(e) => emit({ headerText: e.target.value })}
          placeholder="Header text"
          size="small"
          maxLength={60}
          style={{ marginBottom: 8 }}
        />
      )}

      {/* Body */}
      <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
        Body <span style={{ color: "#ef4444" }}>*</span>{" "}
        <span style={{ color: "#9ca3af" }}>{(data.body || "").length}/1024</span>
      </label>
      <Input.TextArea
        value={data.body || ""}
        onChange={(e) => emit({ body: e.target.value })}
        rows={2}
        maxLength={1024}
        placeholder="Enter the main message for this message type"
        style={{ fontSize: 12, marginBottom: 8 }}
      />

      {/* Footer */}
      <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
        Footer Text (Optional)
      </label>
      <Input
        value={data.footer || ""}
        onChange={(e) => emit({ footer: e.target.value })}
        placeholder="Enter footer text"
        size="small"
        maxLength={60}
        style={{ marginBottom: 8 }}
      />

      {/* Button Label */}
      <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
        Button Label <span style={{ color: "#ef4444" }}>*</span>{" "}
        <span style={{ color: "#9ca3af" }}>{(data.buttonLabel || "").length}/20</span>
      </label>
      <Input
        value={data.buttonLabel || ""}
        onChange={(e) => emit({ buttonLabel: e.target.value })}
        placeholder="Enter button label"
        size="small"
        maxLength={20}
        style={{ marginBottom: 10 }}
      />

      {/* Sections header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 11, color: "#6b7280" }}>
          Sections (atleast one section)
        </span>
        <Button size="small" icon={<PlusOutlined />} onClick={addSection}>
          Add Section
        </Button>
      </div>

      {sections.map((sec, si) => (
        <div
          key={sec.id}
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            padding: 8,
            marginBottom: 8,
            background: "#fafafa",
          }}
        >
          {/* Section title row */}
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: "#6b7280", minWidth: 56 }}>
              Section {si + 1}
            </span>
            <Input
              value={sec.title}
              onChange={(e) => updateSection(si, "title", e.target.value)}
              placeholder="Enter section title"
              size="small"
              maxLength={24}
              style={{ flex: 1 }}
            />
            {sections.length > 1 && (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeSection(si)}
              />
            )}
          </div>

          {/* Rows */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: 10, color: "#9ca3af" }}>Rows (atleast one row)</span>
            <Button size="small" icon={<PlusOutlined />} onClick={() => addRow(si)}>
              Add Row
            </Button>
          </div>

          {(sec.rows || []).map((row, ri) => (
            <div
              key={row.id}
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 4,
                padding: 6,
                marginBottom: 6,
                position: "relative",
              }}
            >
              <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 2 }}>
                    ID <span style={{ color: "#6b7280" }}>{(row.id || "").length}/200</span>
                  </div>
                  <Input
                    value={row.id}
                    onChange={(e) => updateRow(si, ri, "id", e.target.value)}
                    placeholder="Enter ID"
                    size="small"
                    maxLength={200}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 2 }}>
                    Title <span style={{ color: "#6b7280" }}>{(row.title || "").length}/24</span>
                  </div>
                  <Input
                    value={row.title}
                    onChange={(e) => updateRow(si, ri, "title", e.target.value)}
                    placeholder="Enter row title"
                    size="small"
                    maxLength={24}
                  />
                </div>
                {(sec.rows || []).length > 1 && (
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    style={{ marginTop: 16 }}
                    onClick={() => removeRow(si, ri)}
                  />
                )}
              </div>
              <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 2 }}>
                Description <span style={{ color: "#6b7280" }}>{(row.description || "").length}/72</span>
              </div>
              <Input
                value={row.description || ""}
                onChange={(e) => updateRow(si, ri, "description", e.target.value)}
                placeholder="Enter description"
                size="small"
                maxLength={72}
              />
              {/* Each row has its own source handle for flow branching */}
              <Handle
                type="source"
                position={Position.Right}
                id={row.id || `_row_${si}_${ri}`}
                style={{ background: "#06b6d4", right: -14, top: "50%" }}
              />
            </div>
          ))}
        </div>
      ))}
      </div>{/* end nodrag wrapper */}
    </div>
  );
};

export default InteractiveListNode;
